/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOpenURL';

const ActionOpenURLManager: ActionManagerFn<'ActionOpenURL'> = (
  platform: Platform,
): ActionManager<'ActionOpenURL'> => {
  return {
    ...baseActionManager(platform, 'ActionOpenURL'),
    isChildAllowed: (page, item, childType) =>
      ['ActionURLParam'].includes(childType as ItemType),
    defaultChildren: ['ActionURLParam'],
    defaultValue,
    props: {
      ...baseActionProps,
      url: {
        label: 'URL',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      openInNewTab: {
        label: 'Open In New Tab',
        type: 'boolean',
      },
    },
    name: 'Open URL',
    description: `**Overview**<br/>
The Open URL action is a general-purpose navigation component designed to direct the browser to a specific web address. While other navigation actions are optimized for internal application routing, this component is best suited for linking to external websites, third-party services, or resources that exist outside the current single-page application context.<br/><br/>
This action functions as a programmable anchor tag. It allows developers to construct dynamic destination URLs using expressions and manage query parameters cleanly through child components (ActionURLParam), rather than relying on messy string concatenation. It offers control over the browser behavior, allowing the link to open in the current window or a new tab.

**Key Features**<br/>
* **External Navigation:** Optimized for directing users to resources outside the application (e.g., https://google.com), though it can handle any valid URL.
* **Structured Parameters:** Enforces the use of ActionURLParam child actions to define query string parameters. This ensures parameters are properly encoded and appended to the base URL automatically.
* **Target Control:** Features an openInNewTab boolean property, giving developers the choice between a disruption-free redirection (new tab) or a full context switch (same tab).
* **Dynamic Linking:** The url property is an expression, enabling the destination to be calculated at runtime based on user data or application state.

**Properties**<br/>
* **URL (Required):** An expression property returning a string.
  * *Function:* The base destination address (e.g., https://example.com/search).
* **Open In New Tab:** A boolean property.
  * *Function:* If true, the link opens in a new browser tab/window. If false, it navigates the current window.

**Use Cases**<br/>
* **Third-Party Integrations:** Redirecting a user to a payment gateway (like PayPal or Stripe) to complete a transaction.
* **Help Resources:** Opening the company's public documentation or support portal in a new tab so the user doesn't lose their work in the app.
* **Dynamic Reports:** Constructing a URL to an external reporting tool (e.g., PowerBI or Tableau) passing the current user's ID as a parameter via ActionURLParam.`,
  };
};

export default ActionOpenURLManager;
